import { datadogRum } from '@datadog/browser-rum';
import type { IdTokenUserClaim } from '@side/next-auth';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useState } from 'react';
import { useAgentTeam } from '@/common/hooks/useAgentTeam/useAgentTeam';
import { AutofillProvider } from '@/views/Coversheet/components/PlacesAutofill/PlacesAutofill.helpers';
import { useAutofillActionProvider } from '@/views/Coversheet/hooks/useAutofillActionProvider';
import type { CoreRequest } from '$api-clients/hooks';
import {
  appliedFilters,
  type CommunityListingsFilters,
} from './CommunityListings.state';
import type { CommunityListing } from './types';

type CommunityListingsSearchInput = {
  searchCenter?: {
    latitude: number;
    longitude: number;
  };
  searchRadius?: number;
};

export const CommunityListingDBFieldMapper: {
  [label in keyof Partial<CommunityListing>]: string;
} = {
  bedrooms: 'beds',
  bathrooms: 'baths',
  buildingArea: 'sqft',
  lotSize: 'lot_size',
  price: 'listing_price',
  primaryAgent: 'agent',
  dateAdded: 'date_added',
};

export type GeocodeResult = google.maps.GeocoderResult & {
  latitude: number;
  longitude: number;
};

export function useGeocoder() {
  const geocodingLib = useMapsLibrary('geocoding');
  const geocode = useCallback(
    async (request: google.maps.GeocoderRequest) => {
      if (!geocodingLib) {
        throw new Error('Used geocoder before geocoding library was loaded');
      }

      return new Promise<GeocodeResult>((resolve, reject) => {
        const geocoder = new geocodingLib.Geocoder();

        void geocoder.geocode(
          request,
          (results: google.maps.GeocoderResult[] | null, status) => {
            if (
              status !== window.google.maps.GeocoderStatus.OK ||
              results === null
            ) {
              reject(new Error(`Geocoding failed with status ${status}`));
              return;
            }
            const result = results[0];
            resolve({
              ...result,
              latitude: result.geometry.location.lat(),
              longitude: result.geometry.location.lng(),
            });
          },
        );
      });
    },
    [geocodingLib],
  );

  return {
    geocode,
  };
}

function getQueryInput(filters: CommunityListingsFilters) {
  const input: CommunityListingsSearchInput = {};

  if (filters.distance) {
    const { radius, location } = filters.distance;

    input.searchRadius = Number(radius);
    input.searchCenter = {
      latitude: location.latitude,
      longitude: location.longitude,
    };
  }

  return input;
}

export function useFilters() {
  const [filters, setFilters] = useAtom(appliedFilters);
  const [initialized, setInitialized] = useState(false);
  const { team, loading: officeAddressLoading } = useAgentTeam();
  const officeAddress = team?.registeredAddress;
  const searchGooglePlaces = useAutofillActionProvider(
    AutofillProvider.googlePlacesSearch,
    { limitToLicensedStates: true },
  );
  const { geocode } = useGeocoder();

  // Filter by distance from primary office postal code by default
  useEffect(() => {
    if (officeAddressLoading || initialized) return;

    const initializeFilters = async () => {
      if (filters.distance === undefined && officeAddress?.postalCode) {
        const postalCode = `${officeAddress.postalCode}`;
        await new Promise<void>((resolve) => {
          void searchGooglePlaces(
            postalCode,
            async (results: google.maps.places.AutocompletePrediction[]) => {
              const postalCodeResult = results.find((result) =>
                result.types.includes('postal_code'),
              );
              if (!postalCodeResult) {
                return resolve();
              }
              const geocodeRequest = { placeId: postalCodeResult.place_id };
              try {
                const { latitude, longitude } = await geocode(geocodeRequest);
                setFilters((prevFilters) => ({
                  ...prevFilters,
                  distance: {
                    radius: '150',
                    location: {
                      id: postalCodeResult.place_id,
                      label: postalCodeResult.structured_formatting.main_text,
                      secondaryLabel:
                        postalCodeResult.structured_formatting.secondary_text,
                      latitude,
                      longitude,
                    },
                  },
                }));
              } catch (error) {
                datadogRum.addError(
                  'Failed to determine coordinates of postal code',
                  {
                    error,
                    postalCode,
                    geocodeRequest,
                  },
                );
              }
              return resolve();
            },
          );
        });
      }

      setInitialized(true);
    };

    void initializeFilters();
  }, [
    officeAddress,
    officeAddressLoading,
    filters.distance,
    setFilters,
    initialized,
    searchGooglePlaces,
    geocode,
  ]);

  return {
    filters,
    initialized,
    queryInput: getQueryInput(filters),
  };
}

export const isListingEditableByUser = (
  listing: Pick<
    CoreRequest<'/community/listings'>['Data']['results'][number],
    'primaryAgent' | 'coAgents'
  >,
  user: Pick<IdTokenUserClaim, 'id' | 'legacyExternalId'> | undefined,
) => {
  if (!user) {
    return false;
  }

  const agents = [listing.primaryAgent, ...listing.coAgents];

  // User can edit the listing if they are one of the agents associated with it
  return agents.some(
    (agent) =>
      agent.userId === user.id || agent.userId === user.legacyExternalId,
  );
};
